import React, { useState, useEffect } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import LoginPage from "@/components/LoginPage";
import PublicLeaderboard from "@/components/PublicLeaderboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { CalendarIcon, User, TrendingUp, Award, Mail, Plus, Edit, Trash2, Activity, Wifi, WifiOff, RefreshCw, LogOut } from "lucide-react";
import useOffline from "@/hooks/useOffline";
import { getAllFromStore, saveToStore, deleteFromStore, addToSyncQueue, getRecordsByAthleteId, STORES } from "@/utils/offlineStorage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ onLogout }) => {
  const { isOnline, syncStatus, manualSync, manualMode, toggleManualMode, disableManualMode } = useOffline();
  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [records, setRecords] = useState([]);
  const [allRecords, setAllRecords] = useState([]); // All records for leaderboards
  const [isAddAthleteOpen, setIsAddAthleteOpen] = useState(false);
  const [isAddRecordOpen, setIsAddRecordOpen] = useState(false);
  const [isEditAthleteOpen, setIsEditAthleteOpen] = useState(false);
  const [isEditRecordOpen, setIsEditRecordOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [athleteView, setAthleteView] = useState("individual");
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState(null);

  const [athleteForm, setAthleteForm] = useState({
    name: "",
    age: "",
    team: "",
    parent_email: "",
    suggested_video_url_1: "",
    suggested_video_url_2: "",
    suggested_video_url_3: ""
  });

  const [recordForm, setRecordForm] = useState({
    date: new Date(),
    athlete_age: "",
    ten_yard_sprint: "",
    ten_yard_fly: "",
    broad_jump: "",
    vertical_jump: "",
    agility_505: "",
    power_metric_1_name: "",
    power_metric_1_value: "",
    power_metric_2_name: "",
    power_metric_2_value: "",
    power_metric_3_name: "",
    power_metric_3_value: "",
    strength_metric_1_name: "",
    strength_metric_1_value: "",
    strength_metric_2_name: "",
    strength_metric_2_value: "",
    strength_metric_3_name: "",
    strength_metric_3_value: "",
    strength_metric_4_name: "",
    strength_metric_4_value: "",
    strength_metric_5_name: "",
    strength_metric_5_value: ""
  });

  useEffect(() => {
    fetchAthletes();
    fetchAllRecordsForLeaderboard();
  }, []);

  useEffect(() => {
    if (selectedAthlete) {
      fetchAthleteRecords(selectedAthlete.id);
    }
  }, [selectedAthlete]);

  const fetchAllRecordsForLeaderboard = async () => {
    try {
      if (isOnline) {
        const response = await axios.get(`${API}/athletes`);
        const athletesList = response.data;
        
        let allRecordsData = [];
        for (const athlete of athletesList) {
          try {
            const recordsResponse = await axios.get(`${API}/records/athlete/${athlete.id}`);
            const athleteRecords = recordsResponse.data.map(record => ({
              ...record,
              athlete_name: athlete.name
            }));
            allRecordsData = allRecordsData.concat(athleteRecords);
          } catch (error) {
            console.error(`Failed to fetch records for ${athlete.name}`);
          }
        }
        setAllRecords(allRecordsData);
      }
    } catch (error) {
      console.error("Failed to fetch all records:", error);
    }
  };

  const getLeaderboard = (metric, sortOrder = 'asc') => {
    const recordsWithMetric = allRecords.filter(r => r[metric] !== null && r[metric] !== undefined);
    
    // Get the best (most recent) value for each athlete
    const athleteBestMap = {};
    recordsWithMetric.forEach(record => {
      const athleteName = record.athlete_name;
      if (!athleteBestMap[athleteName]) {
        athleteBestMap[athleteName] = {
          value: record[metric],
          date: record.date,
          athlete_name: athleteName
        };
      } else {
        // If sorting ascending (lower is better), take min. Otherwise take max.
        if (sortOrder === 'asc') {
          if (record[metric] < athleteBestMap[athleteName].value) {
            athleteBestMap[athleteName] = {
              value: record[metric],
              date: record.date,
              athlete_name: athleteName
            };
          }
        } else {
          if (record[metric] > athleteBestMap[athleteName].value) {
            athleteBestMap[athleteName] = {
              value: record[metric],
              date: record.date,
              athlete_name: athleteName
            };
          }
        }
      }
    });
    
    // Convert to array and sort
    const leaderboard = Object.values(athleteBestMap);
    leaderboard.sort((a, b) => {
      if (sortOrder === 'asc') {
        return a.value - b.value;
      } else {
        return b.value - a.value;
      }
    });
    
    return leaderboard.slice(0, 10);
  };

  const getLeaderboardByAge = (metric, sortOrder = 'asc') => {
    // Group records by athlete_age (age at time of record)
    const ageGroups = {};
    
    allRecords.forEach(record => {
      if (record[metric] !== null && record[metric] !== undefined) {
        // Use athlete_age from the record (age when record was set)
        // Fall back to current athlete age for backwards compatibility
        let age = record.athlete_age;
        if (age === null || age === undefined) {
          const athlete = athletes.find(a => a.name === record.athlete_name);
          if (athlete) {
            age = athlete.age;
          }
        }
        
        if (age !== null && age !== undefined) {
          if (!ageGroups[age]) {
            ageGroups[age] = [];
          }
          ageGroups[age].push({
            athlete_name: record.athlete_name,
            value: record[metric],
            date: record.date,
            athlete_age: age
          });
        }
      }
    });
    
    // For each age group, get best per athlete then top 10
    const ageLeaderboards = {};
    Object.keys(ageGroups).forEach(age => {
      const athleteBestMap = {};
      
      ageGroups[age].forEach(record => {
        const athleteName = record.athlete_name;
        if (!athleteBestMap[athleteName]) {
          athleteBestMap[athleteName] = record;
        } else {
          if (sortOrder === 'asc') {
            if (record.value < athleteBestMap[athleteName].value) {
              athleteBestMap[athleteName] = record;
            }
          } else {
            if (record.value > athleteBestMap[athleteName].value) {
              athleteBestMap[athleteName] = record;
            }
          }
        }
      });
      
      // Convert to array and sort
      const leaderboard = Object.values(athleteBestMap);
      leaderboard.sort((a, b) => {
        if (sortOrder === 'asc') {
          return a.value - b.value;
        } else {
          return b.value - a.value;
        }
      });
      
      ageLeaderboards[age] = leaderboard.slice(0, 10);
    });
    
    return ageLeaderboards;
  };

  const fetchAthletes = async () => {
    try {
      if (isOnline) {
        // Fetch from server
        const response = await axios.get(`${API}/athletes`);
        const athletesData = response.data;
        setAthletes(athletesData);
        
        // Save to local storage
        for (const athlete of athletesData) {
          await saveToStore(STORES.ATHLETES, athlete);
        }
        
        if (athletesData.length > 0 && !selectedAthlete) {
          setSelectedAthlete(athletesData[0]);
        }
        
        // Refresh leaderboards
        fetchAllRecordsForLeaderboard();
      } else {
        // Load from local storage
        const localAthletes = await getAllFromStore(STORES.ATHLETES);
        setAthletes(localAthletes);
        if (localAthletes.length > 0 && !selectedAthlete) {
          setSelectedAthlete(localAthletes[0]);
        }
      }
    } catch (error) {
      console.error("Fetch athletes error:", error);
      // Try local storage as fallback
      try {
        const localAthletes = await getAllFromStore(STORES.ATHLETES);
        setAthletes(localAthletes);
        if (!isOnline) {
          toast.info("Showing offline data");
        }
      } catch (localError) {
        toast.error("Failed to fetch athletes");
      }
    }
  };

  const fetchAthleteRecords = async (athleteId) => {
    try {
      if (isOnline) {
        // Fetch from server
        const response = await axios.get(`${API}/records/athlete/${athleteId}`);
        const recordsData = response.data;
        setRecords(recordsData);
        
        // Save to local storage
        for (const record of recordsData) {
          await saveToStore(STORES.RECORDS, record);
        }
      } else {
        // Load from local storage
        const localRecords = await getRecordsByAthleteId(athleteId);
        setRecords(localRecords);
      }
    } catch (error) {
      console.error("Fetch records error:", error);
      // Try local storage as fallback
      try {
        const localRecords = await getRecordsByAthleteId(athleteId);
        setRecords(localRecords);
      } catch (localError) {
        toast.error("Failed to fetch records");
      }
    }
  };

  const handleAddAthlete = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const athleteData = {
        id: `temp-${Date.now()}`,
        name: athleteForm.name,
        age: parseInt(athleteForm.age),
        team: athleteForm.team || null,
        parent_email: athleteForm.parent_email || null,
        suggested_video_url_1: athleteForm.suggested_video_url_1 || null,
        suggested_video_url_2: athleteForm.suggested_video_url_2 || null,
        suggested_video_url_3: athleteForm.suggested_video_url_3 || null,
        created_at: new Date().toISOString()
      };

      if (isOnline) {
        // Online: Send to server
        const response = await axios.post(`${API}/athletes`, athleteData);
        const serverAthlete = response.data;
        await saveToStore(STORES.ATHLETES, serverAthlete);
        toast.success("Athlete added successfully");
      } else {
        // Offline: Save locally and queue for sync
        await saveToStore(STORES.ATHLETES, athleteData);
        await addToSyncQueue({
          type: 'CREATE_ATHLETE',
          entity: 'athlete',
          data: athleteData
        });
        toast.info("Athlete saved offline. Will sync when online.");
      }
      
      setIsAddAthleteOpen(false);
      setAthleteForm({ name: "", age: "", team: "", parent_email: "", suggested_video_url_1: "", suggested_video_url_2: "", suggested_video_url_3: "" });
      fetchAthletes();
    } catch (error) {
      toast.error("Failed to add athlete");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAthlete = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const updateData = {
        id: selectedAthlete.id,
        name: athleteForm.name,
        age: parseInt(athleteForm.age),
        team: athleteForm.team || null,
        parent_email: athleteForm.parent_email || null,
        suggested_video_url_1: athleteForm.suggested_video_url_1.trim() || null,
        suggested_video_url_2: athleteForm.suggested_video_url_2.trim() || null,
        suggested_video_url_3: athleteForm.suggested_video_url_3.trim() || null
      };

      if (isOnline) {
        // Online: Send to server
        const response = await axios.put(`${API}/athletes/${selectedAthlete.id}`, updateData);
        const updatedAthlete = response.data;
        await saveToStore(STORES.ATHLETES, updatedAthlete);
        setSelectedAthlete(updatedAthlete);
        toast.success("Athlete updated successfully");
      } else {
        // Offline: Update locally and queue for sync
        const updatedAthlete = { ...selectedAthlete, ...updateData };
        await saveToStore(STORES.ATHLETES, updatedAthlete);
        await addToSyncQueue({
          type: 'UPDATE_ATHLETE',
          entity: 'athlete',
          data: updateData
        });
        setSelectedAthlete(updatedAthlete);
        toast.info("Changes saved offline. Will sync when online.");
      }
      
      setIsEditAthleteOpen(false);
      fetchAthletes();
    } catch (error) {
      toast.error("Failed to update athlete");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAthlete = async (athleteId) => {
    setDeleteTarget(athleteId);
    setDeleteType("athlete");
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteConfirmOpen(false);
    setLoading(true);
    
    try {
      if (deleteType === "athlete") {
        if (isOnline) {
          await axios.delete(`${API}/athletes/${deleteTarget}`);
          await deleteFromStore(STORES.ATHLETES, deleteTarget);
          toast.success("Athlete deleted successfully");
        } else {
          await deleteFromStore(STORES.ATHLETES, deleteTarget);
          await addToSyncQueue({
            type: 'DELETE_ATHLETE',
            entity: 'athlete',
            data: { id: deleteTarget }
          });
          toast.info("Athlete deleted offline. Will sync when online.");
        }
        if (selectedAthlete?.id === deleteTarget) {
          setSelectedAthlete(null);
        }
        await fetchAthletes();
      } else if (deleteType === "record") {
        if (isOnline) {
          await axios.delete(`${API}/records/${deleteTarget}`);
          await deleteFromStore(STORES.RECORDS, deleteTarget);
          toast.success("Record deleted successfully");
        } else {
          await deleteFromStore(STORES.RECORDS, deleteTarget);
          await addToSyncQueue({
            type: 'DELETE_RECORD',
            entity: 'record',
            data: { id: deleteTarget }
          });
          toast.info("Record deleted offline. Will sync when online.");
        }
        await fetchAthleteRecords(selectedAthlete.id);
      } else if (deleteType === "team") {
        const teamAthletes = getTeamGroups()[deleteTarget];
        for (const athlete of teamAthletes) {
          if (isOnline) {
            await axios.delete(`${API}/athletes/${athlete.id}`);
            await deleteFromStore(STORES.ATHLETES, athlete.id);
          } else {
            await deleteFromStore(STORES.ATHLETES, athlete.id);
            await addToSyncQueue({
              type: 'DELETE_ATHLETE',
              entity: 'athlete',
              data: { id: athlete.id }
            });
          }
        }
        toast.success(`Team "${deleteTarget}" deleted ${isOnline ? 'successfully' : 'offline. Will sync when online.'}`);
        setSelectedAthlete(null);
        setSelectedTeam(null);
        await fetchAthletes();
      }
    } catch (error) {
      toast.error("Failed to delete: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
      setDeleteTarget(null);
      setDeleteType(null);
    }
  };

  const handleDeleteTeam = async (teamName) => {
    setDeleteTarget(teamName);
    setDeleteType("team");
    setDeleteConfirmOpen(true);
  };

  const handleAddRecord = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Use the form's athlete_age if set, otherwise use the athlete's current age
      const recordAge = recordForm.athlete_age ? parseInt(recordForm.athlete_age) : selectedAthlete.age;
      
      const payload = {
        id: `temp-record-${Date.now()}`,
        athlete_id: selectedAthlete.id,
        athlete_age: recordAge,
        date: recordForm.date.toISOString(),
        ten_yard_sprint: recordForm.ten_yard_sprint ? parseFloat(recordForm.ten_yard_sprint) : null,
        ten_yard_fly: recordForm.ten_yard_fly ? parseFloat(recordForm.ten_yard_fly) : null,
        broad_jump: recordForm.broad_jump ? parseFloat(recordForm.broad_jump) : null,
        vertical_jump: recordForm.vertical_jump ? parseFloat(recordForm.vertical_jump) : null,
        agility_505: recordForm.agility_505 ? parseFloat(recordForm.agility_505) : null,
        power_metric_1_name: recordForm.power_metric_1_name || null,
        power_metric_1_value: recordForm.power_metric_1_value ? parseFloat(recordForm.power_metric_1_value) : null,
        power_metric_2_name: recordForm.power_metric_2_name || null,
        power_metric_2_value: recordForm.power_metric_2_value ? parseFloat(recordForm.power_metric_2_value) : null,
        power_metric_3_name: recordForm.power_metric_3_name || null,
        power_metric_3_value: recordForm.power_metric_3_value ? parseFloat(recordForm.power_metric_3_value) : null,
        strength_metric_1_name: recordForm.strength_metric_1_name || null,
        strength_metric_1_value: recordForm.strength_metric_1_value ? parseFloat(recordForm.strength_metric_1_value) : null,
        strength_metric_2_name: recordForm.strength_metric_2_name || null,
        strength_metric_2_value: recordForm.strength_metric_2_value ? parseFloat(recordForm.strength_metric_2_value) : null,
        strength_metric_3_name: recordForm.strength_metric_3_name || null,
        strength_metric_3_value: recordForm.strength_metric_3_value ? parseFloat(recordForm.strength_metric_3_value) : null,
        strength_metric_4_name: recordForm.strength_metric_4_name || null,
        strength_metric_4_value: recordForm.strength_metric_4_value ? parseFloat(recordForm.strength_metric_4_value) : null,
        strength_metric_5_name: recordForm.strength_metric_5_name || null,
        strength_metric_5_value: recordForm.strength_metric_5_value ? parseFloat(recordForm.strength_metric_5_value) : null,
        created_at: new Date().toISOString()
      };

      if (isOnline) {
        // Online: Send to server
        const response = await axios.post(`${API}/records`, payload);
        const serverRecord = response.data;
        await saveToStore(STORES.RECORDS, serverRecord);
        toast.success("Record added successfully");
      } else {
        // Offline: Save locally and queue for sync
        await saveToStore(STORES.RECORDS, payload);
        await addToSyncQueue({
          type: 'CREATE_RECORD',
          entity: 'record',
          data: payload
        });
        toast.info("Record saved offline. Will sync when online.");
      }

      setIsAddRecordOpen(false);
      setRecordForm({
        date: new Date(),
        athlete_age: "",
        ten_yard_sprint: "",
        ten_yard_fly: "",
        broad_jump: "",
        vertical_jump: "",
        agility_505: "",
        power_metric_1_name: "",
        power_metric_1_value: "",
        power_metric_2_name: "",
        power_metric_2_value: "",
        power_metric_3_name: "",
        power_metric_3_value: "",
        strength_metric_1_name: "",
        strength_metric_1_value: "",
        strength_metric_2_name: "",
        strength_metric_2_value: "",
        strength_metric_3_name: "",
        strength_metric_3_value: "",
        strength_metric_4_name: "",
        strength_metric_4_value: "",
        strength_metric_5_name: "",
        strength_metric_5_value: ""
      });
      fetchAthleteRecords(selectedAthlete.id);
    } catch (error) {
      toast.error("Failed to add record");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRecord = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Use the form's athlete_age if set, otherwise use the existing record's age or athlete's current age
      const recordAge = recordForm.athlete_age ? parseInt(recordForm.athlete_age) : (editingRecord.athlete_age || selectedAthlete.age);
      
      const payload = {
        id: editingRecord.id,
        athlete_id: editingRecord.athlete_id,
        athlete_age: recordAge,
        date: recordForm.date.toISOString(),
        ten_yard_sprint: recordForm.ten_yard_sprint ? parseFloat(recordForm.ten_yard_sprint) : null,
        ten_yard_fly: recordForm.ten_yard_fly ? parseFloat(recordForm.ten_yard_fly) : null,
        broad_jump: recordForm.broad_jump ? parseFloat(recordForm.broad_jump) : null,
        vertical_jump: recordForm.vertical_jump ? parseFloat(recordForm.vertical_jump) : null,
        agility_505: recordForm.agility_505 ? parseFloat(recordForm.agility_505) : null,
        power_metric_1_name: recordForm.power_metric_1_name || null,
        power_metric_1_value: recordForm.power_metric_1_value ? parseFloat(recordForm.power_metric_1_value) : null,
        power_metric_2_name: recordForm.power_metric_2_name || null,
        power_metric_2_value: recordForm.power_metric_2_value ? parseFloat(recordForm.power_metric_2_value) : null,
        power_metric_3_name: recordForm.power_metric_3_name || null,
        power_metric_3_value: recordForm.power_metric_3_value ? parseFloat(recordForm.power_metric_3_value) : null,
        strength_metric_1_name: recordForm.strength_metric_1_name || null,
        strength_metric_1_value: recordForm.strength_metric_1_value ? parseFloat(recordForm.strength_metric_1_value) : null,
        strength_metric_2_name: recordForm.strength_metric_2_name || null,
        strength_metric_2_value: recordForm.strength_metric_2_value ? parseFloat(recordForm.strength_metric_2_value) : null,
        strength_metric_3_name: recordForm.strength_metric_3_name || null,
        strength_metric_3_value: recordForm.strength_metric_3_value ? parseFloat(recordForm.strength_metric_3_value) : null,
        strength_metric_4_name: recordForm.strength_metric_4_name || null,
        strength_metric_4_value: recordForm.strength_metric_4_value ? parseFloat(recordForm.strength_metric_4_value) : null,
        strength_metric_5_name: recordForm.strength_metric_5_name || null,
        strength_metric_5_value: recordForm.strength_metric_5_value ? parseFloat(recordForm.strength_metric_5_value) : null
      };

      if (isOnline) {
        // Online: Send to server
        await axios.put(`${API}/records/${editingRecord.id}`, payload);
        await saveToStore(STORES.RECORDS, payload);
        toast.success("Record updated successfully");
      } else {
        // Offline: Update locally and queue for sync
        await saveToStore(STORES.RECORDS, payload);
        await addToSyncQueue({
          type: 'UPDATE_RECORD',
          entity: 'record',
          data: payload
        });
        toast.info("Changes saved offline. Will sync when online.");
      }

      setIsEditRecordOpen(false);
      setEditingRecord(null);
      fetchAthleteRecords(selectedAthlete.id);
    } catch (error) {
      toast.error("Failed to update record");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecord = async (recordId) => {
    setDeleteTarget(recordId);
    setDeleteType("record");
    setDeleteConfirmOpen(true);
  };

  const handleSendEmail = async () => {
    if (!selectedAthlete) {
      toast.error("Please select an athlete");
      return;
    }
    if (!selectedAthlete.parent_email) {
      toast.error("No parent email configured for this athlete");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/email/send-report`, {
        athlete_id: selectedAthlete.id
      });
      toast.success("Email sent successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setLoading(false);
    }
  };

  const openEditAthleteDialog = () => {
    if (selectedAthlete) {
      setAthleteForm({
        name: selectedAthlete.name,
        age: selectedAthlete.age.toString(),
        team: selectedAthlete.team || "",
        parent_email: selectedAthlete.parent_email || "",
        suggested_video_url_1: selectedAthlete.suggested_video_url_1 || "",
        suggested_video_url_2: selectedAthlete.suggested_video_url_2 || "",
        suggested_video_url_3: selectedAthlete.suggested_video_url_3 || ""
      });
      setIsEditAthleteOpen(true);
    }
  };

  const openEditRecordDialog = (record) => {
    setEditingRecord(record);
    setRecordForm({
      date: new Date(record.date),
      athlete_age: record.athlete_age?.toString() || selectedAthlete?.age?.toString() || "",
      ten_yard_sprint: record.ten_yard_sprint?.toString() || "",
      ten_yard_fly: record.ten_yard_fly?.toString() || "",
      broad_jump: record.broad_jump?.toString() || "",
      vertical_jump: record.vertical_jump?.toString() || "",
      agility_505: record.agility_505?.toString() || "",
      power_metric_1_name: record.power_metric_1_name || "",
      power_metric_1_value: record.power_metric_1_value?.toString() || "",
      power_metric_2_name: record.power_metric_2_name || "",
      power_metric_2_value: record.power_metric_2_value?.toString() || "",
      power_metric_3_name: record.power_metric_3_name || "",
      power_metric_3_value: record.power_metric_3_value?.toString() || "",
      strength_metric_1_name: record.strength_metric_1_name || "",
      strength_metric_1_value: record.strength_metric_1_value?.toString() || "",
      strength_metric_2_name: record.strength_metric_2_name || "",
      strength_metric_2_value: record.strength_metric_2_value?.toString() || "",
      strength_metric_3_name: record.strength_metric_3_name || "",
      strength_metric_3_value: record.strength_metric_3_value?.toString() || "",
      strength_metric_4_name: record.strength_metric_4_name || "",
      strength_metric_4_value: record.strength_metric_4_value?.toString() || "",
      strength_metric_5_name: record.strength_metric_5_name || "",
      strength_metric_5_value: record.strength_metric_5_value?.toString() || ""
    });
    setIsEditRecordOpen(true);
  };

  const getChartData = (metricKey) => {
    return records
      .filter(record => record[metricKey] !== null && record[metricKey] !== undefined)
      .map(record => ({
        date: format(new Date(record.date), 'MM/dd'),
        value: record[metricKey]
      }));
  };

  const getIndividualAthletes = () => {
    let individualAthletes = athletes.filter(athlete => !athlete.team || athlete.team.trim() === "");
    
    // Always sort alphabetically by name
    individualAthletes.sort((a, b) => a.name.localeCompare(b.name));
    
    return individualAthletes;
  };

  const getTeamGroups = () => {
    const teamMap = {};
    athletes.forEach(athlete => {
      if (athlete.team && athlete.team.trim() !== "") {
        if (!teamMap[athlete.team]) {
          teamMap[athlete.team] = [];
        }
        teamMap[athlete.team].push(athlete);
      }
    });
    
    // Sort athletes within each team alphabetically
    Object.keys(teamMap).forEach(team => {
      teamMap[team].sort((a, b) => a.name.localeCompare(b.name));
    });
    
    return teamMap;
  };

  const getPowerCustomMetrics = (record) => {
    const metrics = [];
    if (record.power_metric_1_name) metrics.push(`${record.power_metric_1_name}: ${record.power_metric_1_value}`);
    if (record.power_metric_2_name) metrics.push(`${record.power_metric_2_name}: ${record.power_metric_2_value}`);
    if (record.power_metric_3_name) metrics.push(`${record.power_metric_3_name}: ${record.power_metric_3_value}`);
    return metrics;
  };

  const getStrengthMetrics = (record) => {
    const metrics = [];
    if (record.strength_metric_1_name) metrics.push(`${record.strength_metric_1_name}: ${record.strength_metric_1_value}`);
    if (record.strength_metric_2_name) metrics.push(`${record.strength_metric_2_name}: ${record.strength_metric_2_value}`);
    if (record.strength_metric_3_name) metrics.push(`${record.strength_metric_3_name}: ${record.strength_metric_3_value}`);
    if (record.strength_metric_4_name) metrics.push(`${record.strength_metric_4_name}: ${record.strength_metric_4_value}`);
    if (record.strength_metric_5_name) metrics.push(`${record.strength_metric_5_name}: ${record.strength_metric_5_value}`);
    return metrics;
  };

  const getBestRecords = () => {
    if (!records || records.length === 0) return null;

    const bestRecords = {};

    // For sprint times, we want the LOWEST (fastest)
    const sprintMetrics = ['ten_yard_sprint', 'ten_yard_fly'];
    // For jumps and other metrics, we want the HIGHEST (greatest)
    const greatestMetrics = ['broad_jump', 'vertical_jump'];

    // Calculate best for standard metrics
    sprintMetrics.forEach(metric => {
      const values = records.filter(r => r[metric] !== null && r[metric] !== undefined).map(r => r[metric]);
      if (values.length > 0) {
        bestRecords[metric] = Math.min(...values);
      }
    });

    greatestMetrics.forEach(metric => {
      const values = records.filter(r => r[metric] !== null && r[metric] !== undefined).map(r => r[metric]);
      if (values.length > 0) {
        bestRecords[metric] = Math.max(...values);
      }
    });

    // Calculate best for custom power metrics (greatest)
    for (let i = 1; i <= 3; i++) {
      const nameKey = `power_metric_${i}_name`;
      const valueKey = `power_metric_${i}_value`;
      const recordsWithMetric = records.filter(r => r[nameKey] && r[valueKey] !== null && r[valueKey] !== undefined);
      if (recordsWithMetric.length > 0) {
        const bestValue = Math.max(...recordsWithMetric.map(r => r[valueKey]));
        const recordWithBest = recordsWithMetric.find(r => r[valueKey] === bestValue);
        bestRecords[nameKey] = recordWithBest[nameKey];
        bestRecords[valueKey] = bestValue;
      }
    }

    // Calculate best for custom strength metrics (greatest)
    for (let i = 1; i <= 5; i++) {
      const nameKey = `strength_metric_${i}_name`;
      const valueKey = `strength_metric_${i}_value`;
      const recordsWithMetric = records.filter(r => r[nameKey] && r[valueKey] !== null && r[valueKey] !== undefined);
      if (recordsWithMetric.length > 0) {
        const bestValue = Math.max(...recordsWithMetric.map(r => r[valueKey]));
        const recordWithBest = recordsWithMetric.find(r => r[valueKey] === bestValue);
        bestRecords[nameKey] = recordWithBest[nameKey];
        bestRecords[valueKey] = bestValue;
      }
    }

    return bestRecords;
  };

  // Calculate Armory Explosiveness Score (AES)
  // AES = 50 * (2 / 10y) + 30 * (BJ / 60) + 20 * (VJ / 14)
  const calculateAES = (tenYardSprint, broadJump, verticalJump) => {
    if (tenYardSprint === null || tenYardSprint === undefined || 
        broadJump === null || broadJump === undefined || 
        verticalJump === null || verticalJump === undefined) {
      return null;
    }
    
    const speedComponent = 50 * (2 / tenYardSprint);
    const broadJumpComponent = 30 * (broadJump / 60);
    const verticalJumpComponent = 20 * (verticalJump / 14);
    
    const aes = speedComponent + broadJumpComponent + verticalJumpComponent;
    return Math.round(aes * 100) / 100; // Round to 2 decimal places
  };

  // Get athlete's best AES score from their records
  const getAthleteAES = () => {
    const bestRecords = getBestRecords();
    if (!bestRecords) return null;
    
    return calculateAES(
      bestRecords.ten_yard_sprint,
      bestRecords.broad_jump,
      bestRecords.vertical_jump
    );
  };

  // Get AES leaderboard (overall top 10)
  const getAESLeaderboard = () => {
    const athleteAESMap = {};
    
    allRecords.forEach(record => {
      const aes = calculateAES(record.ten_yard_sprint, record.broad_jump, record.vertical_jump);
      if (aes !== null) {
        const athleteName = record.athlete_name;
        if (!athleteAESMap[athleteName] || aes > athleteAESMap[athleteName].value) {
          athleteAESMap[athleteName] = {
            value: aes,
            athlete_name: athleteName,
            ten_yard_sprint: record.ten_yard_sprint,
            broad_jump: record.broad_jump,
            vertical_jump: record.vertical_jump
          };
        }
      }
    });
    
    const leaderboard = Object.values(athleteAESMap);
    leaderboard.sort((a, b) => b.value - a.value); // Higher AES is better
    return leaderboard.slice(0, 10);
  };

  // Get AES leaderboard by age
  const getAESLeaderboardByAge = () => {
    const ageGroups = {};
    
    allRecords.forEach(record => {
      const aes = calculateAES(record.ten_yard_sprint, record.broad_jump, record.vertical_jump);
      if (aes !== null) {
        // Use athlete_age from record, fall back to current athlete age
        let age = record.athlete_age;
        if (age === null || age === undefined) {
          const athlete = athletes.find(a => a.name === record.athlete_name);
          if (athlete) {
            age = athlete.age;
          }
        }
        
        if (age !== null && age !== undefined) {
          if (!ageGroups[age]) {
            ageGroups[age] = {};
          }
          
          const athleteName = record.athlete_name;
          if (!ageGroups[age][athleteName] || aes > ageGroups[age][athleteName].value) {
            ageGroups[age][athleteName] = {
              value: aes,
              athlete_name: athleteName,
              athlete_age: age
            };
          }
        }
      }
    });
    
    // Convert to sorted leaderboards
    const ageLeaderboards = {};
    Object.keys(ageGroups).forEach(age => {
      const leaderboard = Object.values(ageGroups[age]);
      leaderboard.sort((a, b) => b.value - a.value);
      ageLeaderboards[age] = leaderboard.slice(0, 10);
    });
    
    return ageLeaderboards;
  };

  const formatBestRecords = (bestRecords) => {
    if (!bestRecords || Object.keys(bestRecords).length === 0) return null;

    const parts = [];

    // Standard metrics
    if (bestRecords.ten_yard_sprint) parts.push(`10Y Sprint: ${bestRecords.ten_yard_sprint}s`);
    if (bestRecords.ten_yard_fly) parts.push(`10Y Fly: ${bestRecords.ten_yard_fly}s`);
    if (bestRecords.broad_jump) parts.push(`Broad Jump: ${bestRecords.broad_jump}"`);
    if (bestRecords.vertical_jump) parts.push(`Vertical Jump: ${bestRecords.vertical_jump}"`);

    // Custom power metrics
    for (let i = 1; i <= 3; i++) {
      const nameKey = `power_metric_${i}_name`;
      const valueKey = `power_metric_${i}_value`;
      if (bestRecords[nameKey]) {
        parts.push(`${bestRecords[nameKey]}: ${bestRecords[valueKey]}`);
      }
    }

    // Custom strength metrics
    for (let i = 1; i <= 5; i++) {
      const nameKey = `strength_metric_${i}_name`;
      const valueKey = `strength_metric_${i}_value`;
      if (bestRecords[nameKey]) {
        parts.push(`${bestRecords[nameKey]}: ${bestRecords[valueKey]}`);
      }
    }

    return parts.join(' | ');
  };

  const groupRecordsByDate = () => {
    if (!records || records.length === 0) return [];

    // Group records by date
    const dateMap = {};
    records.forEach(record => {
      const dateKey = format(new Date(record.date), 'MM/dd/yyyy');
      if (!dateMap[dateKey]) {
        dateMap[dateKey] = [];
      }
      dateMap[dateKey].push(record);
    });

    // For each date, combine records and show best values
    const combinedRecords = [];
    Object.keys(dateMap).forEach(dateKey => {
      const dateRecords = dateMap[dateKey];
      
      if (dateRecords.length === 1) {
        // Only one record for this date, use it as-is
        combinedRecords.push(dateRecords[0]);
      } else {
        // Multiple records for this date, combine them
        const combined = { ...dateRecords[0] }; // Start with first record
        
        // For sprint times, take LOWEST (fastest)
        const sprintMetrics = ['ten_yard_sprint', 'ten_yard_fly'];
        sprintMetrics.forEach(metric => {
          const values = dateRecords.filter(r => r[metric] !== null && r[metric] !== undefined).map(r => r[metric]);
          if (values.length > 0) {
            combined[metric] = Math.min(...values);
          }
        });
        
        // For jumps and other metrics, take HIGHEST (greatest)
        const greatestMetrics = ['broad_jump', 'vertical_jump'];
        greatestMetrics.forEach(metric => {
          const values = dateRecords.filter(r => r[metric] !== null && r[metric] !== undefined).map(r => r[metric]);
          if (values.length > 0) {
            combined[metric] = Math.max(...values);
          }
        });
        
        // For custom power metrics, take HIGHEST
        for (let i = 1; i <= 3; i++) {
          const nameKey = `power_metric_${i}_name`;
          const valueKey = `power_metric_${i}_value`;
          
          // Collect all unique metric names for this slot
          const metricData = {};
          dateRecords.forEach(r => {
            if (r[nameKey] && r[valueKey] !== null && r[valueKey] !== undefined) {
              if (!metricData[r[nameKey]]) {
                metricData[r[nameKey]] = [];
              }
              metricData[r[nameKey]].push(r[valueKey]);
            }
          });
          
          // Take the highest value for each unique metric name
          if (Object.keys(metricData).length > 0) {
            const metricName = Object.keys(metricData)[0]; // Use first metric name found
            combined[nameKey] = metricName;
            combined[valueKey] = Math.max(...metricData[metricName]);
          }
        }
        
        // For custom strength metrics, take HIGHEST
        for (let i = 1; i <= 5; i++) {
          const nameKey = `strength_metric_${i}_name`;
          const valueKey = `strength_metric_${i}_value`;
          
          const metricData = {};
          dateRecords.forEach(r => {
            if (r[nameKey] && r[valueKey] !== null && r[valueKey] !== undefined) {
              if (!metricData[r[nameKey]]) {
                metricData[r[nameKey]] = [];
              }
              metricData[r[nameKey]].push(r[valueKey]);
            }
          });
          
          if (Object.keys(metricData).length > 0) {
            const metricName = Object.keys(metricData)[0];
            combined[nameKey] = metricName;
            combined[valueKey] = Math.max(...metricData[metricName]);
          }
        }
        
        combinedRecords.push(combined);
      }
    });

    return combinedRecords;
  };

  // Group records by age for displaying in athlete profile
  const groupRecordsByAge = () => {
    if (!records || records.length === 0) return {};

    const ageGroups = {};
    records.forEach(record => {
      // Use athlete_age from record, fall back to selected athlete's current age
      const age = record.athlete_age || selectedAthlete?.age || 'Unknown';
      if (!ageGroups[age]) {
        ageGroups[age] = [];
      }
      ageGroups[age].push(record);
    });

    // Sort ages in descending order (newest age first)
    const sortedAges = Object.keys(ageGroups).sort((a, b) => parseInt(b) - parseInt(a));
    const sortedGroups = {};
    sortedAges.forEach(age => {
      sortedGroups[age] = ageGroups[age];
    });

    return sortedGroups;
  };

  // Get unique ages from records
  const getRecordAges = () => {
    if (!records || records.length === 0) return [];
    const ages = [...new Set(records.map(r => r.athlete_age || selectedAthlete?.age).filter(a => a !== undefined && a !== null))];
    return ages.sort((a, b) => parseInt(b) - parseInt(a));
  };

  const renderRecordForm = (isEdit = false) => (
    <form onSubmit={isEdit ? handleUpdateRecord : handleAddRecord} className="space-y-4 max-h-[70vh] overflow-y-auto">
      <div>
        <Label>Date</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start" data-testid="record-date-picker">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {format(recordForm.date, 'PPP')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={recordForm.date}
              onSelect={(date) => setRecordForm({ ...recordForm, date: date || new Date() })}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
      
      <div className="border-t pt-4">
        <h4 className="font-semibold mb-3 text-lg text-slate-700">Power Assessments</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="ten_yard_sprint">10 Yard Sprint (sec)</Label>
            <Input
              id="ten_yard_sprint"
              type="number"
              step="0.01"
              data-testid="ten-yard-sprint-input"
              value={recordForm.ten_yard_sprint}
              onChange={(e) => setRecordForm({ ...recordForm, ten_yard_sprint: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="ten_yard_fly">10 Yard Fly (sec)</Label>
            <Input
              id="ten_yard_fly"
              type="number"
              step="0.01"
              data-testid="ten-yard-fly-input"
              value={recordForm.ten_yard_fly}
              onChange={(e) => setRecordForm({ ...recordForm, ten_yard_fly: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="broad_jump">Broad Jump (in)</Label>
            <Input
              id="broad_jump"
              type="number"
              step="0.01"
              data-testid="broad-jump-input"
              value={recordForm.broad_jump}
              onChange={(e) => setRecordForm({ ...recordForm, broad_jump: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="vertical_jump">Vertical Jump (in)</Label>
            <Input
              id="vertical_jump"
              type="number"
              step="0.01"
              data-testid="vertical-jump-input"
              value={recordForm.vertical_jump}
              onChange={(e) => setRecordForm({ ...recordForm, vertical_jump: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="agility_505">505 Agility (sec)</Label>
            <Input
              id="agility_505"
              type="number"
              step="0.01"
              data-testid="agility-505-input"
              value={recordForm.agility_505}
              onChange={(e) => setRecordForm({ ...recordForm, agility_505: e.target.value })}
            />
          </div>
        </div>
        
        <h5 className="font-medium mt-4 mb-2 text-sm">Additional Power Metrics (Optional)</h5>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="grid grid-cols-2 gap-4">
              <Input
                placeholder={`Metric ${i} name`}
                value={recordForm[`power_metric_${i}_name`]}
                onChange={(e) => setRecordForm({ ...recordForm, [`power_metric_${i}_name`]: e.target.value })}
              />
              <Input
                type="number"
                step="0.01"
                placeholder={`Value`}
                value={recordForm[`power_metric_${i}_value`]}
                onChange={(e) => setRecordForm({ ...recordForm, [`power_metric_${i}_value`]: e.target.value })}
              />
            </div>
          ))}
        </div>
      </div>
      
      <div className="border-t pt-4">
        <h4 className="font-semibold mb-3 text-lg text-slate-700">Strength Assessments</h4>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="grid grid-cols-2 gap-4">
              <Input
                placeholder={`Strength metric ${i} name`}
                value={recordForm[`strength_metric_${i}_name`]}
                onChange={(e) => setRecordForm({ ...recordForm, [`strength_metric_${i}_name`]: e.target.value })}
              />
              <Input
                type="number"
                step="0.01"
                placeholder={`Value`}
                value={recordForm[`strength_metric_${i}_value`]}
                onChange={(e) => setRecordForm({ ...recordForm, [`strength_metric_${i}_value`]: e.target.value })}
              />
            </div>
          ))}
        </div>
      </div>
      
      {/* Athlete Age for this Record */}
      <div className="border-t pt-4">
        <h4 className="font-semibold mb-3 text-lg text-slate-700">Athlete Age for this Record</h4>
        <div className="bg-slate-50 p-4 rounded-lg">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label htmlFor="athlete_age">Age at time of record</Label>
              <Input
                id="athlete_age"
                type="number"
                min="1"
                max="99"
                data-testid="athlete-age-input"
                placeholder={selectedAthlete?.age?.toString() || ""}
                value={recordForm.athlete_age}
                onChange={(e) => setRecordForm({ ...recordForm, athlete_age: e.target.value })}
              />
            </div>
            <div className="text-sm text-gray-500 flex-1">
              <p><strong>Profile Age:</strong> {selectedAthlete?.age || 'N/A'}</p>
              <p className="text-xs mt-1">Leave blank to use profile age. Override if the athlete's age was different when this record was set.</p>
            </div>
          </div>
        </div>
      </div>
      
      <DialogFooter>
        <Button type="submit" disabled={loading} data-testid={isEdit ? "update-record-btn" : "submit-record-btn"}>
          {loading ? (isEdit ? "Updating..." : "Adding...") : (isEdit ? "Update Record" : "Add Record")}
        </Button>
      </DialogFooter>
    </form>
  );

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #3d4756 0%, #2c3442 100%)' }}>
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_athlete-dashboard-3/artifacts/tm6xtcdu_496240925_122106843224865340_513732478382055889_n.jpg" 
              alt="The Armory - Strength & Speed" 
              className="h-24 w-auto"
              data-testid="armory-logo"
            />
          </div>
          <div className="flex items-center justify-between w-full">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="dashboard-title">
                Athlete Performance Dashboard
              </h1>
              <p className="text-lg text-white/90" data-testid="dashboard-subtitle">Track, analyze, and share athlete performance data</p>
            </div>
            <div className="flex items-center gap-3">
              {/* Online/Offline Indicator with Manual Toggle */}
              <div className="flex flex-col gap-2">
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isOnline ? 'bg-green-500/20 border border-green-500' : 'bg-red-500/20 border border-red-500'}`}>
                  {isOnline ? (
                    <>
                      <Wifi size={20} className="text-green-400" />
                      <span className="text-sm font-medium text-white">Online</span>
                    </>
                  ) : (
                    <>
                      <WifiOff size={20} className="text-red-400" />
                      <span className="text-sm font-medium text-white">Offline</span>
                    </>
                  )}
                  {manualMode && (
                    <span className="text-xs text-white/70 ml-1">(Manual)</span>
                  )}
                </div>
                
                {/* Manual Mode Toggle Buttons */}
                <div className="flex gap-2">
                  {!manualMode ? (
                    <>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => toggleManualMode(true)}
                        className="text-xs bg-red-500/10 border-red-500 hover:bg-red-500/20 text-white"
                      >
                        Go Offline
                      </Button>
                    </>
                  ) : (
                    <>
                      {!isOnline && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => toggleManualMode(false)}
                          className="text-xs bg-green-500/10 border-green-500 hover:bg-green-500/20 text-white"
                        >
                          Go Online
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={disableManualMode}
                        className="text-xs bg-blue-500/10 border-blue-500 hover:bg-blue-500/20 text-white"
                      >
                        Auto Mode
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              {/* Sync Button and Pending Counter */}
              {(syncStatus.pendingCount > 0 || syncStatus.syncing) && (
                <div className="flex items-center gap-2">
                  <div className="px-3 py-2 rounded-lg bg-yellow-500/20 border border-yellow-500">
                    <span className="text-sm font-medium text-white">
                      {syncStatus.pendingCount} pending
                    </span>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => manualSync().catch(err => toast.error("Sync failed"))}
                    disabled={!isOnline || syncStatus.syncing}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <RefreshCw size={16} className={`mr-2 ${syncStatus.syncing ? 'animate-spin' : ''}`} />
                    {syncStatus.syncing ? 'Syncing...' : 'Sync Now'}
                  </Button>
                </div>
              )}
              
              {/* Logout Button */}
              <Button 
                size="sm" 
                variant="outline"
                onClick={onLogout}
                className="bg-white/10 border-white/20 hover:bg-white/20 text-white"
              >
                <LogOut size={16} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <Card className="backdrop-blur-xl bg-white/95 shadow-2xl border-0" data-testid="athlete-list-card">
              <CardHeader>
                <CardTitle className="flex items-center justify-between" data-testid="athletes-title">
                  <span className="flex items-center">
                    <User className="mr-2" size={20} />
                    Athletes
                  </span>
                  <Dialog open={isAddAthleteOpen} onOpenChange={setIsAddAthleteOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800" data-testid="add-athlete-btn">
                        <Plus size={16} />
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="add-athlete-dialog">
                      <DialogHeader>
                        <DialogTitle data-testid="add-athlete-dialog-title">Add New Athlete</DialogTitle>
                        <DialogDescription>Enter athlete details and parent contact information</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleAddAthlete} className="space-y-4">
                        <div>
                          <Label htmlFor="name">Name</Label>
                          <Input
                            id="name"
                            data-testid="athlete-name-input"
                            value={athleteForm.name}
                            onChange={(e) => setAthleteForm({ ...athleteForm, name: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="age">Age</Label>
                          <Input
                            id="age"
                            type="number"
                            data-testid="athlete-age-input"
                            value={athleteForm.age}
                            onChange={(e) => setAthleteForm({ ...athleteForm, age: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="team">Team (Optional)</Label>
                          <Input
                            id="team"
                            data-testid="athlete-team-input"
                            value={athleteForm.team}
                            onChange={(e) => setAthleteForm({ ...athleteForm, team: e.target.value })}
                            placeholder="e.g., Varsity, JV, U17"
                          />
                        </div>
                        <div>
                          <Label htmlFor="parent_email">Parent Email (Optional)</Label>
                          <Input
                            id="parent_email"
                            type="email"
                            data-testid="athlete-email-input"
                            value={athleteForm.parent_email}
                            onChange={(e) => setAthleteForm({ ...athleteForm, parent_email: e.target.value })}
                            placeholder="Required for email reports"
                          />
                        </div>
                        <div>
                          <Label>Suggested Links for Home (Optional)</Label>
                          <div className="space-y-2">
                            <Input
                              type="url"
                              data-testid="athlete-video-1-input"
                              value={athleteForm.suggested_video_url_1}
                              onChange={(e) => setAthleteForm({ ...athleteForm, suggested_video_url_1: e.target.value })}
                              placeholder="Link 1: https://example.com/..."
                            />
                            <Input
                              type="url"
                              data-testid="athlete-video-2-input"
                              value={athleteForm.suggested_video_url_2}
                              onChange={(e) => setAthleteForm({ ...athleteForm, suggested_video_url_2: e.target.value })}
                              placeholder="Link 2: https://example.com/..."
                            />
                            <Input
                              type="url"
                              data-testid="athlete-video-3-input"
                              value={athleteForm.suggested_video_url_3}
                              onChange={(e) => setAthleteForm({ ...athleteForm, suggested_video_url_3: e.target.value })}
                              placeholder="Link 3: https://example.com/..."
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Links to exercises or resources for home practice</p>
                        </div>
                        <DialogFooter>
                          <Button type="submit" disabled={loading} data-testid="submit-athlete-btn">
                            {loading ? "Adding..." : "Add Athlete"}
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3" data-testid="athlete-list">
                {athletes.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4" data-testid="no-athletes-message">No athletes yet. Add one to get started!</p>
                ) : (
                  <>
                    <div className="flex gap-1 mb-4 flex-wrap">
                      <Button
                        variant={athleteView === "individual" ? "default" : "outline"}
                        size="sm"
                        onClick={() => { setAthleteView("individual"); setSelectedTeam(null); }}
                        className={`text-xs ${athleteView === "individual" ? "bg-slate-600 hover:bg-slate-700" : ""}`}
                        data-testid="individual-athletes-btn"
                      >
                        Individual
                      </Button>
                      <Button
                        variant={athleteView === "team" ? "default" : "outline"}
                        size="sm"
                        onClick={() => { setAthleteView("team"); setSelectedTeam(null); }}
                        className={`text-xs ${athleteView === "team" ? "bg-slate-600 hover:bg-slate-700" : ""}`}
                        data-testid="team-athletes-btn"
                      >
                        Teams
                      </Button>
                      <Button
                        variant={athleteView === "byAge" ? "default" : "outline"}
                        size="sm"
                        onClick={() => { setAthleteView("byAge"); setSelectedAthlete(null); setSelectedTeam(null); }}
                        className={`text-xs ${athleteView === "byAge" ? "bg-slate-600 hover:bg-slate-700" : ""}`}
                        data-testid="by-age-btn"
                      >
                        By Age
                      </Button>
                      <Button
                        variant={athleteView === "leaderboard" ? "default" : "outline"}
                        size="sm"
                        onClick={() => { setAthleteView("leaderboard"); setSelectedAthlete(null); setSelectedTeam(null); }}
                        className={`text-xs ${athleteView === "leaderboard" ? "bg-slate-600 hover:bg-slate-700" : ""}`}
                        data-testid="leaderboard-btn"
                      >
                        Last Entry
                      </Button>
                      <Button
                        variant={athleteView === "top10" ? "default" : "outline"}
                        size="sm"
                        onClick={() => { setAthleteView("top10"); setSelectedAthlete(null); setSelectedTeam(null); }}
                        className={`text-xs ${athleteView === "top10" ? "bg-slate-600 hover:bg-slate-700" : ""}`}
                        data-testid="top10-btn"
                      >
                        Top 10
                      </Button>
                    </div>

                    {athleteView === "individual" && (
                      <div className="space-y-2">
                        {getIndividualAthletes().length > 0 && (
                          <div className="mb-2">
                            <Label className="text-xs text-gray-600 mb-1">Select Athlete</Label>
                            <Select 
                              value={selectedAthlete?.id || ""} 
                              onValueChange={(athleteId) => {
                                const athlete = getIndividualAthletes().find(a => a.id === athleteId);
                                if (athlete) setSelectedAthlete(athlete);
                              }}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Choose an athlete" />
                              </SelectTrigger>
                              <SelectContent>
                                {getIndividualAthletes().map((athlete) => (
                                  <SelectItem key={athlete.id} value={athlete.id}>
                                    {athlete.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        
                        {getIndividualAthletes().length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">No individual athletes</p>
                        ) : (
                          getIndividualAthletes().map((athlete) => (
                            <div
                              key={athlete.id}
                              className={`p-3 rounded-lg hover:bg-slate-50 border-2 ${
                                selectedAthlete?.id === athlete.id ? 'bg-slate-100 border-slate-600' : 'bg-white border-transparent'
                              }`}
                              data-testid={`athlete-item-${athlete.id}`}
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1 cursor-pointer" onClick={() => setSelectedAthlete(athlete)}>
                                  <div className="font-medium" data-testid={`athlete-name-${athlete.id}`}>{athlete.name}</div>
                                  <div className="text-sm text-gray-500" data-testid={`athlete-age-${athlete.id}`}>Age: {athlete.age}</div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteAthlete(athlete.id);
                                  }}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 h-auto"
                                  data-testid={`delete-athlete-${athlete.id}`}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {athleteView === "team" && (
                      <div className="space-y-2">
                        {Object.keys(getTeamGroups()).length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">No team athletes</p>
                        ) : selectedTeam ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedTeam(null)}
                              className="mb-2"
                              data-testid="back-to-teams-btn"
                            >
                              ← Back to Teams
                            </Button>
                            <div className="space-y-2">
                              {getTeamGroups()[selectedTeam].map((athlete) => (
                                <div
                                  key={athlete.id}
                                  className={`p-3 rounded-lg hover:bg-slate-50 border-2 ${
                                    selectedAthlete?.id === athlete.id ? 'bg-slate-100 border-slate-600' : 'bg-white border-transparent'
                                  }`}
                                  data-testid={`athlete-item-${athlete.id}`}
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1 cursor-pointer" onClick={() => setSelectedAthlete(athlete)}>
                                      <div className="font-medium" data-testid={`athlete-name-${athlete.id}`}>{athlete.name}</div>
                                      <div className="text-sm text-gray-500" data-testid={`athlete-age-${athlete.id}`}>Age: {athlete.age}</div>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteAthlete(athlete.id);
                                      }}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 h-auto"
                                      data-testid={`delete-athlete-${athlete.id}`}
                                    >
                                      <Trash2 size={14} />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          Object.keys(getTeamGroups()).map((teamName) => (
                            <div
                              key={teamName}
                              className="p-3 rounded-lg hover:bg-slate-50 border-2 bg-white border-transparent"
                              data-testid={`team-group-${teamName}`}
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1 cursor-pointer" onClick={() => setSelectedTeam(teamName)}>
                                  <div className="font-medium">{teamName}</div>
                                  <div className="text-sm text-gray-500">{getTeamGroups()[teamName].length} athletes</div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteTeam(teamName);
                                  }}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 h-auto"
                                  data-testid={`delete-team-${teamName}`}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {athleteView === "leaderboard" && (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-slate-700 mb-3">Top 10 Leaderboards</h3>
                        
                        {/* 10 Yard Sprint Leaderboard */}
                        <div className="border-l-4 border-purple-500 pl-3">
                          <h4 className="font-semibold text-purple-700 mb-2">⚡ 10 Yard Sprint (Fastest)</h4>
                          <div className="space-y-1">
                            {getLeaderboard('ten_yard_sprint', 'asc').map((entry, index) => (
                              <div key={`sprint-${index}`} className="flex justify-between text-sm py-1 px-2 rounded hover:bg-purple-50">
                                <span className="font-medium">{index + 1}. {entry.athlete_name}</span>
                                <span className="text-purple-600 font-bold">{entry.value}s</span>
                              </div>
                            ))}
                            {getLeaderboard('ten_yard_sprint', 'asc').length === 0 && (
                              <p className="text-xs text-gray-500 italic">No records yet</p>
                            )}
                          </div>
                        </div>

                        {/* 10 Yard Fly Leaderboard */}
                        <div className="border-l-4 border-purple-500 pl-3">
                          <h4 className="font-semibold text-purple-700 mb-2">⚡ 10 Yard Fly (Fastest)</h4>
                          <div className="space-y-1">
                            {getLeaderboard('ten_yard_fly', 'asc').map((entry, index) => (
                              <div key={`fly-${index}`} className="flex justify-between text-sm py-1 px-2 rounded hover:bg-purple-50">
                                <span className="font-medium">{index + 1}. {entry.athlete_name}</span>
                                <span className="text-purple-600 font-bold">{entry.value}s</span>
                              </div>
                            ))}
                            {getLeaderboard('ten_yard_fly', 'asc').length === 0 && (
                              <p className="text-xs text-gray-500 italic">No records yet</p>
                            )}
                          </div>
                        </div>

                        {/* Broad Jump Leaderboard */}
                        <div className="border-l-4 border-blue-500 pl-3">
                          <h4 className="font-semibold text-blue-700 mb-2">⚡ Broad Jump (Highest)</h4>
                          <div className="space-y-1">
                            {getLeaderboard('broad_jump', 'desc').map((entry, index) => (
                              <div key={`broad-${index}`} className="flex justify-between text-sm py-1 px-2 rounded hover:bg-blue-50">
                                <span className="font-medium">{index + 1}. {entry.athlete_name}</span>
                                <span className="text-blue-600 font-bold">{entry.value}"</span>
                              </div>
                            ))}
                            {getLeaderboard('broad_jump', 'desc').length === 0 && (
                              <p className="text-xs text-gray-500 italic">No records yet</p>
                            )}
                          </div>
                        </div>

                        {/* Vertical Jump Leaderboard */}
                        <div className="border-l-4 border-blue-500 pl-3">
                          <h4 className="font-semibold text-blue-700 mb-2">⚡ Vertical Jump (Highest)</h4>
                          <div className="space-y-1">
                            {getLeaderboard('vertical_jump', 'desc').map((entry, index) => (
                              <div key={`vertical-${index}`} className="flex justify-between text-sm py-1 px-2 rounded hover:bg-blue-50">
                                <span className="font-medium">{index + 1}. {entry.athlete_name}</span>
                                <span className="text-blue-600 font-bold">{entry.value}"</span>
                              </div>
                            ))}
                            {getLeaderboard('vertical_jump', 'desc').length === 0 && (
                              <p className="text-xs text-gray-500 italic">No records yet</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {athleteView === "top10" && (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-slate-700 mb-3">Top 10 Performers</h3>
                        
                        {/* 10 Yard Sprint Leaderboard */}
                        <div className="border-l-4 border-purple-500 pl-3">
                          <h4 className="font-semibold text-purple-700 mb-2">⚡ 10 Yard Sprint (Top 10 Lowest)</h4>
                          <div className="space-y-1">
                            {getLeaderboard('ten_yard_sprint', 'asc').map((entry, index) => (
                              <div key={`sprint-${index}`} className="flex justify-between text-sm py-1 px-2 rounded hover:bg-purple-50">
                                <span className="font-medium">{index + 1}. {entry.athlete_name}</span>
                                <span className="text-purple-600 font-bold">{entry.value}s</span>
                              </div>
                            ))}
                            {getLeaderboard('ten_yard_sprint', 'asc').length === 0 && (
                              <p className="text-xs text-gray-500 italic">No records yet</p>
                            )}
                          </div>
                        </div>

                        {/* 10 Yard Fly Leaderboard */}
                        <div className="border-l-4 border-purple-500 pl-3">
                          <h4 className="font-semibold text-purple-700 mb-2">⚡ 10 Yard Fly (Top 10 Lowest)</h4>
                          <div className="space-y-1">
                            {getLeaderboard('ten_yard_fly', 'asc').map((entry, index) => (
                              <div key={`fly-${index}`} className="flex justify-between text-sm py-1 px-2 rounded hover:bg-purple-50">
                                <span className="font-medium">{index + 1}. {entry.athlete_name}</span>
                                <span className="text-purple-600 font-bold">{entry.value}s</span>
                              </div>
                            ))}
                            {getLeaderboard('ten_yard_fly', 'asc').length === 0 && (
                              <p className="text-xs text-gray-500 italic">No records yet</p>
                            )}
                          </div>
                        </div>

                        {/* Broad Jump Leaderboard */}
                        <div className="border-l-4 border-blue-500 pl-3">
                          <h4 className="font-semibold text-blue-700 mb-2">⚡ Broad Jump (Top 10 Highest)</h4>
                          <div className="space-y-1">
                            {getLeaderboard('broad_jump', 'desc').map((entry, index) => (
                              <div key={`broad-${index}`} className="flex justify-between text-sm py-1 px-2 rounded hover:bg-blue-50">
                                <span className="font-medium">{index + 1}. {entry.athlete_name}</span>
                                <span className="text-blue-600 font-bold">{entry.value}"</span>
                              </div>
                            ))}
                            {getLeaderboard('broad_jump', 'desc').length === 0 && (
                              <p className="text-xs text-gray-500 italic">No records yet</p>
                            )}
                          </div>
                        </div>

                        {/* Vertical Jump Leaderboard */}
                        <div className="border-l-4 border-blue-500 pl-3">
                          <h4 className="font-semibold text-blue-700 mb-2">⚡ Vertical Jump (Top 10 Highest)</h4>
                          <div className="space-y-1">
                            {getLeaderboard('vertical_jump', 'desc').map((entry, index) => (
                              <div key={`vertical-${index}`} className="flex justify-between text-sm py-1 px-2 rounded hover:bg-blue-50">
                                <span className="font-medium">{index + 1}. {entry.athlete_name}</span>
                                <span className="text-blue-600 font-bold">{entry.value}"</span>
                              </div>
                            ))}
                            {getLeaderboard('vertical_jump', 'desc').length === 0 && (
                              <p className="text-xs text-gray-500 italic">No records yet</p>
                            )}
                          </div>
                        </div>
                        
                        {/* AES Leaderboard */}
                        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-500 rounded-lg p-3">
                          <h4 className="font-semibold text-amber-700 mb-2">⚡ Armory Explosive Score - AES (Top 10 Highest)</h4>
                          <p className="text-xs text-gray-500 mb-2">10yd Sprint, Broad Jump, Vertical Jump</p>
                          <div className="space-y-1">
                            {getAESLeaderboard().map((entry, index) => (
                              <div key={`aes-${index}`} className="flex justify-between text-sm py-1 px-2 rounded hover:bg-amber-100">
                                <span className="font-medium">{index + 1}. {entry.athlete_name}</span>
                                <span className="text-amber-700 font-bold">{entry.value}</span>
                              </div>
                            ))}
                            {getAESLeaderboard().length === 0 && (
                              <p className="text-xs text-gray-500 italic">No complete records yet (requires 10Y Sprint, Broad Jump, and Vertical Jump)</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {athleteView === "byAge" && (
                      <div className="space-y-6">
                        <h3 className="text-lg font-semibold text-slate-700 mb-3">Top 10 by Age Group</h3>
                        
                        {/* 10 Yard Sprint by Age */}
                        <div className="border-2 border-purple-500 rounded-lg p-3">
                          <h4 className="font-bold text-purple-700 mb-3">⚡ 10 Yard Sprint (Fastest by Age)</h4>
                          {Object.entries(getLeaderboardByAge('ten_yard_sprint', 'asc'))
                            .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                            .map(([age, athletes]) => (
                            <div key={`sprint-age-${age}`} className="mb-3 bg-purple-50 rounded p-2">
                              <p className="font-semibold text-sm text-purple-900 mb-1">Age {age}</p>
                              <div className="space-y-1 pl-2">
                                {athletes.map((entry, index) => (
                                  <div key={`sprint-${age}-${index}`} className="flex justify-between text-xs">
                                    <span>{index + 1}. {entry.athlete_name}</span>
                                    <span className="font-bold text-purple-600">{entry.value}s</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* 10 Yard Fly by Age */}
                        <div className="border-2 border-purple-500 rounded-lg p-3">
                          <h4 className="font-bold text-purple-700 mb-3">⚡ 10 Yard Fly (Fastest by Age)</h4>
                          {Object.entries(getLeaderboardByAge('ten_yard_fly', 'asc'))
                            .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                            .map(([age, athletes]) => (
                            <div key={`fly-age-${age}`} className="mb-3 bg-purple-50 rounded p-2">
                              <p className="font-semibold text-sm text-purple-900 mb-1">Age {age}</p>
                              <div className="space-y-1 pl-2">
                                {athletes.map((entry, index) => (
                                  <div key={`fly-${age}-${index}`} className="flex justify-between text-xs">
                                    <span>{index + 1}. {entry.athlete_name}</span>
                                    <span className="font-bold text-purple-600">{entry.value}s</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Broad Jump by Age */}
                        <div className="border-2 border-blue-500 rounded-lg p-3">
                          <h4 className="font-bold text-blue-700 mb-3">⚡ Broad Jump (Highest by Age)</h4>
                          {Object.entries(getLeaderboardByAge('broad_jump', 'desc'))
                            .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                            .map(([age, athletes]) => (
                            <div key={`broad-age-${age}`} className="mb-3 bg-blue-50 rounded p-2">
                              <p className="font-semibold text-sm text-blue-900 mb-1">Age {age}</p>
                              <div className="space-y-1 pl-2">
                                {athletes.map((entry, index) => (
                                  <div key={`broad-${age}-${index}`} className="flex justify-between text-xs">
                                    <span>{index + 1}. {entry.athlete_name}</span>
                                    <span className="font-bold text-blue-600">{entry.value}"</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Vertical Jump by Age */}
                        <div className="border-2 border-blue-500 rounded-lg p-3">
                          <h4 className="font-bold text-blue-700 mb-3">⚡ Vertical Jump (Highest by Age)</h4>
                          {Object.entries(getLeaderboardByAge('vertical_jump', 'desc'))
                            .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                            .map(([age, athletes]) => (
                            <div key={`vertical-age-${age}`} className="mb-3 bg-blue-50 rounded p-2">
                              <p className="font-semibold text-sm text-blue-900 mb-1">Age {age}</p>
                              <div className="space-y-1 pl-2">
                                {athletes.map((entry, index) => (
                                  <div key={`vertical-${age}-${index}`} className="flex justify-between text-xs">
                                    <span>{index + 1}. {entry.athlete_name}</span>
                                    <span className="font-bold text-blue-600">{entry.value}"</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* AES by Age */}
                        <div className="border-2 border-amber-500 rounded-lg p-3 bg-gradient-to-r from-amber-50 to-orange-50">
                          <h4 className="font-bold text-amber-700 mb-3">⚡ Armory Explosive Score - AES (Highest by Age)</h4>
                          <p className="text-xs text-gray-500 mb-2">10yd Sprint, Broad Jump, Vertical Jump</p>
                          {Object.entries(getAESLeaderboardByAge())
                            .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                            .map(([age, athletes]) => (
                            <div key={`aes-age-${age}`} className="mb-3 bg-amber-100 rounded p-2">
                              <p className="font-semibold text-sm text-amber-900 mb-1">Age {age}</p>
                              <div className="space-y-1 pl-2">
                                {athletes.map((entry, index) => (
                                  <div key={`aes-${age}-${index}`} className="flex justify-between text-xs">
                                    <span>{index + 1}. {entry.athlete_name}</span>
                                    <span className="font-bold text-amber-700">{entry.value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                          {Object.keys(getAESLeaderboardByAge()).length === 0 && (
                            <p className="text-xs text-gray-500 italic">No complete records yet (requires 10Y Sprint, Broad Jump, and Vertical Jump)</p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-3">
            {athleteView === "leaderboard" ? (
              <Card className="backdrop-blur-xl bg-white/95 shadow-2xl border-0">
                <CardHeader>
                  <CardTitle className="text-2xl">Top Performers</CardTitle>
                  <CardDescription>Best records across all athletes</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-center py-8">
                    Select a leaderboard from the left to view details, or view all top 10s in the sidebar.
                  </p>
                </CardContent>
              </Card>
            ) : athleteView === "top10" ? (
              <Card className="backdrop-blur-xl bg-white/95 shadow-2xl border-0">
                <CardHeader>
                  <CardTitle className="text-2xl">Top 10 Leaderboards</CardTitle>
                  <CardDescription>Best performers across all metrics</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-center py-8">
                    View the top 10 performers for each metric in the sidebar.
                  </p>
                </CardContent>
              </Card>
            ) : athleteView === "byAge" ? (
              <Card className="backdrop-blur-xl bg-white/95 shadow-2xl border-0">
                <CardHeader>
                  <CardTitle className="text-2xl">Top 10 by Age Group</CardTitle>
                  <CardDescription>Best performers in each age category</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-center py-8">
                    View the top 10 performers for each age group in the sidebar.
                  </p>
                </CardContent>
              </Card>
            ) : selectedAthlete ? (
              <div className="space-y-6">
                <Card className="backdrop-blur-xl bg-white/95 shadow-2xl border-0" data-testid="athlete-info-card">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-3xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="selected-athlete-name">{selectedAthlete.name}</CardTitle>
                        <CardDescription className="mt-2" data-testid="selected-athlete-details">
                          Age: {selectedAthlete.age}{selectedAthlete.team && ` | Team: ${selectedAthlete.team}`}{selectedAthlete.parent_email && ` | Parent: ${selectedAthlete.parent_email}`}
                        </CardDescription>
                        {(() => {
                          const bestRecords = getBestRecords();
                          const formattedBest = formatBestRecords(bestRecords);
                          const aesScore = getAthleteAES();
                          return (formattedBest || aesScore !== null) && (
                            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md" data-testid="best-records-section">
                              <p className="text-xs font-semibold text-green-900 mb-1">🏆 Best Records</p>
                              {formattedBest && <p className="text-xs text-green-800" data-testid="best-records-text">{formattedBest}</p>}
                              {aesScore !== null && (
                                <div className="mt-2 pt-2 border-t border-green-200">
                                  <p className="text-sm font-bold text-green-900" data-testid="aes-score">
                                    ⚡ Armory Explosive Score (AES): <span className="text-lg">{aesScore}</span>
                                  </p>
                                  <p className="text-xs text-green-700 mt-1">10yd Sprint, Broad Jump, Vertical Jump</p>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        {(selectedAthlete.suggested_video_url_1 || selectedAthlete.suggested_video_url_2 || selectedAthlete.suggested_video_url_3) && (
                          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md" data-testid="suggested-video-section">
                            <p className="text-sm font-medium text-yellow-900 mb-2">Suggested Links for Home</p>
                            <div className="space-y-1">
                              {selectedAthlete.suggested_video_url_1 && (
                                <a 
                                  href={selectedAthlete.suggested_video_url_1} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:text-blue-800 underline break-all block"
                                  data-testid="suggested-video-link-1"
                                >
                                  🔗 Link 1: {selectedAthlete.suggested_video_url_1}
                                </a>
                              )}
                              {selectedAthlete.suggested_video_url_2 && (
                                <a 
                                  href={selectedAthlete.suggested_video_url_2} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:text-blue-800 underline break-all block"
                                  data-testid="suggested-video-link-2"
                                >
                                  🔗 Link 2: {selectedAthlete.suggested_video_url_2}
                                </a>
                              )}
                              {selectedAthlete.suggested_video_url_3 && (
                                <a 
                                  href={selectedAthlete.suggested_video_url_3} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:text-blue-800 underline break-all block"
                                  data-testid="suggested-video-link-3"
                                >
                                  🔗 Link 3: {selectedAthlete.suggested_video_url_3}
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={openEditAthleteDialog} data-testid="edit-athlete-btn">
                            <Edit size={16} className="mr-1" /> Edit
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDeleteAthlete(selectedAthlete.id)} className="text-red-600 hover:text-red-700" data-testid="delete-athlete-btn">
                            <Trash2 size={16} className="mr-1" /> Delete
                          </Button>
                        </div>
                        <Button onClick={handleSendEmail} disabled={loading || records.length === 0 || !selectedAthlete.parent_email} className="bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 w-full" data-testid="send-email-btn">
                          <Mail size={16} className="mr-2" />
                          {loading ? "Sending..." : "Email Report"}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                <Tabs defaultValue="records" className="w-full" data-testid="performance-tabs">
                  <TabsList className="grid w-full grid-cols-2 bg-white/80 backdrop-blur" data-testid="tabs-list">
                    <TabsTrigger value="records" data-testid="records-tab">Performance Records</TabsTrigger>
                    <TabsTrigger value="charts" data-testid="charts-tab">Progress Charts</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="records" className="space-y-4" data-testid="records-content">
                    <Card className="backdrop-blur-xl bg-white/95 shadow-2xl border-0">
                      <CardHeader>
                        <div className="flex justify-between items-center">
                          <CardTitle data-testid="records-title">Performance History</CardTitle>
                          <Dialog open={isAddRecordOpen} onOpenChange={setIsAddRecordOpen}>
                            <DialogTrigger asChild>
                              <Button className="bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800" data-testid="add-record-btn">
                                <Plus size={16} className="mr-2" /> Add Record
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden" data-testid="add-record-dialog">
                              <DialogHeader>
                                <DialogTitle data-testid="add-record-dialog-title">Add Performance Record</DialogTitle>
                                <DialogDescription>Enter performance metrics for {selectedAthlete.name}</DialogDescription>
                              </DialogHeader>
                              {renderRecordForm(false)}
                            </DialogContent>
                          </Dialog>
                        </div>
                      </CardHeader>
                      <CardContent data-testid="records-table-container">
                        {records.length === 0 ? (
                          <div className="text-center py-12" data-testid="no-records-message">
                            <Award size={48} className="mx-auto mb-4 text-gray-300" />
                            <p className="text-gray-500">No performance records yet. Add one to start tracking!</p>
                          </div>
                        ) : (
                          <div className="space-y-6">
                            {Object.entries(groupRecordsByAge()).map(([age, ageRecords]) => (
                              <div key={`age-group-${age}`} className="border rounded-lg overflow-hidden" data-testid={`age-group-${age}`}>
                                <div className="bg-slate-100 px-4 py-2 border-b">
                                  <h4 className="font-semibold text-slate-700">Age {age} Records</h4>
                                </div>
                                <div className="overflow-x-auto">
                                  <Table data-testid={`records-table-age-${age}`}>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead data-testid="table-header-date">Date</TableHead>
                                        <TableHead data-testid="table-header-assessments">Assessments</TableHead>
                                        <TableHead data-testid="table-header-actions">Actions</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {ageRecords.map((record) => {
                                        const powerCustom = getPowerCustomMetrics(record);
                                        const strengthMetrics = getStrengthMetrics(record);
                                        
                                        return (
                                          <TableRow key={record.id} data-testid={`record-row-${record.id}`}>
                                            <TableCell data-testid={`record-date-${record.id}`} className="font-medium">
                                              {format(new Date(record.date), 'MM/dd/yyyy')}
                                            </TableCell>
                                            <TableCell data-testid={`record-assessments-${record.id}`}>
                                              <div className="space-y-1">
                                                <div className="font-medium text-sm">
                                                  <span className="text-purple-700">Power:</span>{' '}
                                                  {record.ten_yard_sprint && `10Y Sprint: ${record.ten_yard_sprint}s`}
                                                  {record.ten_yard_fly && ` | 10Y Fly: ${record.ten_yard_fly}s`}
                                                  {record.broad_jump && ` | Broad: ${record.broad_jump}in`}
                                                  {record.vertical_jump && ` | Vertical: ${record.vertical_jump}in`}
                                                  {record.agility_505 && ` | 505 Agility: ${record.agility_505}s`}
                                                  {!record.ten_yard_sprint && !record.ten_yard_fly && !record.broad_jump && !record.vertical_jump && !record.agility_505 && '-'}
                                                </div>
                                                {(powerCustom.length > 0 || strengthMetrics.length > 0) && (
                                                  <div className="text-xs text-gray-600">
                                                    {powerCustom.length > 0 && (
                                                      <div><span className="font-medium">Power+:</span> {powerCustom.join(' | ')}</div>
                                                    )}
                                                    {strengthMetrics.length > 0 && (
                                                      <div><span className="font-medium text-green-700">Strength:</span> {strengthMetrics.join(' | ')}</div>
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                            </TableCell>
                                            <TableCell>
                                              <div className="flex gap-2">
                                                <Button variant="ghost" size="sm" onClick={() => openEditRecordDialog(record)} data-testid={`edit-record-btn-${record.id}`}>
                                                  <Edit size={14} />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => handleDeleteRecord(record.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-record-btn-${record.id}`}>
                                                  <Trash2 size={14} />
                                                </Button>
                                              </div>
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  <TabsContent value="charts" data-testid="charts-content">
                    <div className="space-y-6">
                      {records.length === 0 ? (
                        <Card className="backdrop-blur-xl bg-white/95 shadow-2xl border-0">
                          <CardContent className="text-center py-12" data-testid="no-charts-message">
                            <TrendingUp size={48} className="mx-auto mb-4 text-gray-300" />
                            <p className="text-gray-500">Add performance records to see progress charts</p>
                          </CardContent>
                        </Card>
                      ) : (
                        <>
                          {['ten_yard_sprint', 'ten_yard_fly', 'broad_jump', 'vertical_jump'].map(metric => {
                            const data = getChartData(metric);
                            if (data.length === 0) return null;
                            
                            const metricNames = {
                              ten_yard_sprint: '10 Yard Sprint (seconds)',
                              ten_yard_fly: '10 Yard Fly (seconds)',
                              broad_jump: 'Broad Jump (inches)',
                              vertical_jump: 'Vertical Jump (inches)'
                            };
                            
                            return (
                              <Card key={metric} className="backdrop-blur-xl bg-white/95 shadow-2xl border-0">
                                <CardHeader>
                                  <CardTitle className="flex items-center text-lg">
                                    <TrendingUp className="mr-2" size={18} />
                                    {metricNames[metric]}
                                  </CardTitle>
                                </CardHeader>
                                <CardContent>
                                  <ResponsiveContainer width="100%" height={250}>
                                    <LineChart data={data}>
                                      <CartesianGrid strokeDasharray="3 3" />
                                      <XAxis dataKey="date" />
                                      <YAxis />
                                      <Tooltip />
                                      <Legend />
                                      <Line type="monotone" dataKey="value" stroke="#8b5cf6" strokeWidth={2} name={metricNames[metric]} />
                                    </LineChart>
                                  </ResponsiveContainer>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            ) : (
              <Card className="backdrop-blur-xl bg-white/95 shadow-2xl border-0" data-testid="no-athlete-selected">
                <CardContent className="flex flex-col items-center justify-center py-24">
                  <User size={64} className="text-gray-300 mb-4" />
                  <h3 className="text-2xl font-semibold text-gray-700 mb-2">No Athlete Selected</h3>
                  <p className="text-gray-500">Select an athlete from the list or add a new one to get started</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      <Dialog open={isEditAthleteOpen} onOpenChange={setIsEditAthleteOpen}>
        <DialogContent data-testid="edit-athlete-dialog">
          <DialogHeader>
            <DialogTitle data-testid="edit-athlete-dialog-title">Edit Athlete</DialogTitle>
            <DialogDescription>Update athlete details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateAthlete} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                data-testid="edit-athlete-name-input"
                value={athleteForm.name}
                onChange={(e) => setAthleteForm({ ...athleteForm, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-age">Age</Label>
              <Input
                id="edit-age"
                type="number"
                data-testid="edit-athlete-age-input"
                value={athleteForm.age}
                onChange={(e) => setAthleteForm({ ...athleteForm, age: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-team">Team (Optional)</Label>
              <Input
                id="edit-team"
                data-testid="edit-athlete-team-input"
                value={athleteForm.team}
                onChange={(e) => setAthleteForm({ ...athleteForm, team: e.target.value })}
                placeholder="e.g., Varsity, JV, U17"
              />
            </div>
            <div>
              <Label htmlFor="edit-parent_email">Parent Email (Optional)</Label>
              <Input
                id="edit-parent_email"
                type="email"
                data-testid="edit-athlete-email-input"
                value={athleteForm.parent_email}
                onChange={(e) => setAthleteForm({ ...athleteForm, parent_email: e.target.value })}
                placeholder="Required for email reports"
              />
            </div>
            <div>
              <Label>Suggested Links for Home (Optional)</Label>
              <div className="space-y-2">
                <Input
                  type="url"
                  data-testid="edit-athlete-video-1-input"
                  value={athleteForm.suggested_video_url_1}
                  onChange={(e) => setAthleteForm({ ...athleteForm, suggested_video_url_1: e.target.value })}
                  placeholder="Link 1: https://example.com/..."
                />
                <Input
                  type="url"
                  data-testid="edit-athlete-video-2-input"
                  value={athleteForm.suggested_video_url_2}
                  onChange={(e) => setAthleteForm({ ...athleteForm, suggested_video_url_2: e.target.value })}
                  placeholder="Link 2: https://example.com/..."
                />
                <Input
                  type="url"
                  data-testid="edit-athlete-video-3-input"
                  value={athleteForm.suggested_video_url_3}
                  onChange={(e) => setAthleteForm({ ...athleteForm, suggested_video_url_3: e.target.value })}
                  placeholder="Link 3: https://example.com/..."
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Links to exercises or resources for home practice. Leave blank to remove.</p>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={loading} data-testid="update-athlete-btn">
                {loading ? "Updating..." : "Update Athlete"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditRecordOpen} onOpenChange={setIsEditRecordOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden" data-testid="edit-record-dialog">
          <DialogHeader>
            <DialogTitle data-testid="edit-record-dialog-title">Edit Performance Record</DialogTitle>
            <DialogDescription>Update performance metrics</DialogDescription>
          </DialogHeader>
          {renderRecordForm(true)}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              {deleteType === "athlete" && "Are you sure you want to delete this athlete and all their records? This action cannot be undone."}
              {deleteType === "record" && "Are you sure you want to delete this performance record? This action cannot be undone."}
              {deleteType === "team" && `Are you sure you want to delete all ${getTeamGroups()[deleteTarget]?.length || 0} athletes in team "${deleteTarget}" and all their records? This action cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} data-testid="cancel-delete-btn">
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {loading ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    const authStatus = localStorage.getItem("armory_authenticated");
    const authExpiry = localStorage.getItem("armory_auth_expiry");
    
    if (authStatus === "true" && authExpiry) {
      const expiryDate = new Date(authExpiry);
      const now = new Date();
      
      // Check if session is still valid
      if (now < expiryDate) {
        setIsAuthenticated(true);
      } else {
        // Session expired, clear storage
        localStorage.removeItem("armory_authenticated");
        localStorage.removeItem("armory_auth_expiry");
        setIsAuthenticated(false);
      }
    }
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("armory_authenticated");
    localStorage.removeItem("armory_auth_expiry");
    setIsAuthenticated(false);
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route 
            path="/" 
            element={
              isAuthenticated ? (
                <Dashboard onLogout={handleLogout} />
              ) : (
                <PublicLeaderboard onLogin={handleLogin} />
              )
            } 
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
